import React, { useEffect, useState } from "react";
import { Table, Input, Modal } from "antd";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import client from "../../api";
import routes from "src/config/routes";
import { logger } from "src/lib/utils";

import { PlusIcon } from "src/components/icons/plus-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { DeleteIcon } from "src/components/icons/delete-icon";
import { permissions as permissionConstants } from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";

const CardTermsConditions = () => {
  const navigate = useNavigate();

  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions(
    permissionConstants.CARD_TERMS_CONDITIONS
  );
  const canViewTerms = canView || canList;

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  // ===========================
  // LIST API
  // ===========================

  const { mutate: getTermsList, isLoading } = useMutation(
    client.cardTermsConditions.list,
    {
      onSuccess: (res: any) => {
        logger.log("Card Terms List", res);

        const list = res?.data?.data || [];

        setDataSource(
          list.map((item: any) => ({
            ...item,
            key: item.id,
          })),
        );

        setTotal(res?.data?.total || 0);
      },
    },
  );

  // ===========================
  // DELETE API
  // ===========================

  const { mutate: deleteTerm } = useMutation(
    client.cardTermsConditions.delete,
    {
      onSuccess: (res) => {
        logger.log("Delete Response", res);

        getTermsList({
          page,
          limit,
          search: debouncedSearch || undefined,
        });
      },
    },
  );

  // ===========================
  // DELETE CONFIRM
  // ===========================

  const onDelete = (id: number) => {
    if (!canDelete) return;
    Modal.confirm({
      title: "Delete",
      content: "Are you sure you want to delete this record?",
      okText: "Yes",
      cancelText: "No",
      okType: "danger",

      onOk() {
        deleteTerm({ id });
      },
    });
  };

  // ===========================
  // LIST CALL
  // ===========================

  useEffect(() => {
    if (!canViewTerms) return;
    getTermsList({
      page,
      limit,
      search: debouncedSearch || undefined,
    });
  }, [canViewTerms, page, limit, debouncedSearch]);

  // ===========================
  // SEARCH
  // ===========================

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // ===========================
  // COLUMNS
  // ===========================

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 70,
    },

    {
      title: "Product",
      dataIndex: "product",
      width: 220,
      render: (product: any) => (
        <div className="flex flex-col">
          <span className="font-semibold">{product?.name || "-"}</span>
          <span className="text-xs text-gray-500">{product?.code || "-"}</span>
        </div>
      ),
    },

    {
      title: "Package",
      dataIndex: "package_type",
      width: 120,
      render: (value: string) => (
        <span className="capitalize">{value?.toLowerCase()}</span>
      ),
    },

    {
      title: "Resident Fee",
      dataIndex: "resident_fee",
      width: 120,
      render: (value: string) => `₾ ${value}`,
    },

    {
      title: "Non Resident Fee",
      dataIndex: "non_resident_fee",
      width: 150,
      render: (value: string) => `₾ ${value}`,
    },

    {
      title: "Description",
      dataIndex: "description",
      width: 220,
      render: (description: any) =>
        description ? (
          <>
            <div>{description.title}</div>
            <div>{description.subtitle}</div>
          </>
        ) : (
          "-"
        ),
    },

    // {
    //   title: "Card Conditions",
    //   dataIndex: "card_condition_url",
    //   width: 180,
    //   render: (url: string) =>
    //     url ? (
    //       <a
    //         href={url}
    //         target="_blank"
    //         rel="noopener noreferrer"
    //         className="text-primary underline"
    //       >
    //         View
    //       </a>
    //     ) : (
    //       "-"
    //     ),
    // },

    // {
    //   title: "Terms & Conditions",
    //   dataIndex: "terms_and_conditions_url",
    //   width: 190,
    //   render: (url: string) =>
    //     url ? (
    //       <a
    //         href={url}
    //         target="_blank"
    //         rel="noopener noreferrer"
    //         className="text-primary underline"
    //       >
    //         View
    //       </a>
    //     ) : (
    //       "-"
    //     ),
    // },

    {
      title: "Status",
      dataIndex: "active",
      width: 100,
      render: (active: boolean) => (
        <span
          className={
            active ? "text-green-600 font-medium" : "text-red-500 font-medium"
          }
        >
          {active ? "Active" : "Inactive"}
        </span>
      ),
    },

    {
      title: "Created",
      dataIndex: "created_at",
      width: 170,
      render: (value: string) => moment(value).format("DD MMM YYYY HH:mm"),
    },

    {
      title: "Action",
      key: "action",
      width: 100,
      fixed: "right" as const,
      render: (_: any, record: any) => {
        if (!canEdit && !canDelete) {
          return "—";
        }
        return (
          <div className="flex gap-2">
            {canEdit && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center"
                onClick={() => navigate(`/card-terms-conditions/edit/${record.id}`)}
              >
                <EditIcon className="w-[18px] h-[18px] text-primary" />
              </button>
            )}

            {canDelete && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center"
                onClick={() => onDelete(record.id)}
              >
                <DeleteIcon className="w-[18px] h-[18px] text-primary" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-6 bg-white rounded-lg">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl font-semibold">Card Terms & Conditions</h2>

        {/* <Input.Search
          allowClear
          placeholder="Search..."
          value={searchText}
          style={{ width: 320 }}
          onChange={(e) => setSearchText(e.target.value)}
        /> */}

        <PermissionGate
          module={[permissionConstants.CARD_TERMS_CONDITIONS]}
          action="can_add"
        >
          <button
            className="flex items-center gap-2 bg-white hover:bg-[#E6E8EC] rounded px-4 py-2"
            onClick={() => navigate(routes.cardTermsConditionsForm)}
          >
            <span className="font-medium text-primary">Add</span>

            <PlusIcon className="w-[18px] h-[18px] text-primary" />
          </button>
        </PermissionGate>
      </div>
      {!canViewTerms ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view card terms & conditions.
          </p>
        </div>
      ) : (
        <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={dataSource}
            scroll={{ x: 1700 }}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              position: ["bottomCenter"],
              onChange: (newPage, newLimit) => {
                setPage(newPage);
                setLimit(newLimit);
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CardTermsConditions;
